import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/utils";
import { Package, X } from "lucide-react";
import "./ProductImageLoupe.css";

const NAVY = "#102a43";
const BLUE = "#1266dc";
const LINE = "#dfe7f0";
const MUTED = "#64748b";

/** Same magnifier strength as Buy Source thumbs. */
export const PRODUCT_IMAGE_MAGNIFIER_ZOOM = 2.4;

function isTouchDevice() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(hover: none) and (pointer: coarse)").matches
    || "ontouchstart" in window
  );
}

function canHoverFinePointer() {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return true;
  }
  return window.matchMedia("(hover: hover) and (pointer: fine)").matches;
}

function cssUrl(src) {
  return `url("${String(src).replace(/\\/g, "\\\\").replace(/"/g, '\\"')}")`;
}

/**
 * Portal magnifier — same review UX as Buy Source image cards.
 * Fixed positioning so parent overflow cannot clip the zoom panel.
 */
function ProductImageMagnifierPreview({
  anchorRef,
  src,
  open,
  title,
  focal = { x: 50, y: 50 },
  compact = false,
}) {
  const [coords, setCoords] = useState(null);

  const updatePosition = useCallback(() => {
    const el = anchorRef?.current;
    if (!el || !open) {
      setCoords(null);
      return;
    }
    const rect = el.getBoundingClientRect();
    const pad = 8;
    const vw = typeof window !== "undefined" ? window.innerWidth : 1200;
    const previewW = compact
      ? Math.min(280, Math.max(220, Math.round(vw * 0.2)))
      : Math.min(320, Math.max(260, Math.round(vw * 0.22)));
    const previewH = Math.round(previewW * 1.05);
    let left = rect.left + rect.width / 2 - previewW / 2;
    left = Math.min(Math.max(left, pad), window.innerWidth - pad - previewW);
    let top = rect.top - previewH - 12;
    if (top < pad) {
      top = Math.min(rect.bottom + 12, window.innerHeight - pad - previewH);
    }
    setCoords({ left, top, width: previewW, height: previewH });
  }, [anchorRef, open, compact]);

  useLayoutEffect(() => {
    updatePosition();
  }, [updatePosition, src, focal.x, focal.y]);

  useEffect(() => {
    if (!open) return undefined;
    const onScrollOrResize = () => updatePosition();
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [open, updatePosition]);

  if (!open || !src || !coords || typeof document === "undefined") return null;

  const bgSize = `${PRODUCT_IMAGE_MAGNIFIER_ZOOM * 100}%`;
  const bgPos = `${focal.x}% ${focal.y}%`;
  const showTitle = Boolean(title && String(title).trim());

  return createPortal(
    <div
      className="product-image-loupe-preview"
      data-testid="product-image-loupe-preview"
      data-magnifier="true"
      data-zoom={String(PRODUCT_IMAGE_MAGNIFIER_ZOOM)}
      aria-hidden
      style={{
        position: "fixed",
        left: coords.left,
        top: coords.top,
        width: coords.width,
        height: coords.height,
        pointerEvents: "none",
        zIndex: 10040,
      }}
    >
      <div
        className="product-image-loupe-preview__zoom"
        data-testid="product-image-loupe-preview-zoom"
        style={{
          backgroundImage: cssUrl(src),
          backgroundSize: bgSize,
          backgroundPosition: bgPos,
          backgroundRepeat: "no-repeat",
        }}
      />
      {showTitle ? (
        <div
          className="product-image-loupe-preview__title"
          data-testid="product-image-loupe-preview-title"
        >
          {title}
        </div>
      ) : null}
    </div>,
    document.body,
  );
}

/**
 * Shared product image loupe — Buy Source–style hover magnifier + touch modal.
 * Used by Product Analyzer and Storefront Hunter.
 */
export function ProductImageLoupe({
  images = [],
  title = "",
  size = 143,
  zoomPanelSize = 154,
  lensSize = 52,
  zoomBackgroundSize = 358,
  showThumbnails = true,
  showNav = true,
  className,
  testId,
  placeholder = null,
  /** When false, image is decorative and must not intercept parent clicks/cursors. */
  interactive = true,
}) {
  const [index, setIndex] = useState(0);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [focal, setFocal] = useState({ x: 50, y: 50 });
  const [modalOpen, setModalOpen] = useState(false);
  const mainRef = useRef(null);
  const touch = useMemo(() => isTouchDevice(), []);

  const list = Array.isArray(images) ? images.filter(Boolean) : [];
  const listKey = list.join("|");
  const hasImages = list.length > 0;
  const multi = list.length > 1;
  const current = hasImages ? list[((index % list.length) + list.length) % list.length] : null;
  const compact = size <= 72;

  useEffect(() => {
    setIndex(0);
    setPreviewOpen(false);
    setFocal({ x: 50, y: 50 });
  }, [listKey]);

  const show = useCallback(
    (i) => {
      if (!list.length) return;
      setIndex(((i % list.length) + list.length) % list.length);
    },
    [list.length],
  );

  const updateFocalFromEvent = (e) => {
    const el = mainRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    if (!r.width || !r.height) return;
    const x = ((e.clientX - r.left) / r.width) * 100;
    const y = ((e.clientY - r.top) / r.height) * 100;
    setFocal({
      x: Math.min(100, Math.max(0, x)),
      y: Math.min(100, Math.max(0, y)),
    });
  };

  const onMove = (e) => {
    if (!interactive || !hasImages || touch) return;
    if (!canHoverFinePointer()) return;
    updateFocalFromEvent(e);
    setPreviewOpen(true);
  };

  const onEnter = (e) => {
    if (!interactive || !hasImages || touch) return;
    if (!canHoverFinePointer()) return;
    updateFocalFromEvent(e);
    setPreviewOpen(true);
  };

  const onLeave = () => {
    setPreviewOpen(false);
  };

  const onMainClick = (e) => {
    if (!interactive) return;
    e.stopPropagation();
    if (hasImages && touch) setModalOpen(true);
  };

  useEffect(() => {
    if (!modalOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") setModalOpen(false);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [modalOpen]);

  // Keep unused props referenced so callers (SFH / Analyzer) stay compatible.
  void zoomPanelSize;
  void lensSize;
  void zoomBackgroundSize;

  const onGalleryKeyDown = useCallback(
    (e) => {
      if (!interactive || !hasImages) return;
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        show(index - 1);
        return;
      }
      if (e.key === "ArrowRight") {
        e.preventDefault();
        show(index + 1);
        return;
      }
      if (touch && (e.key === "Enter" || e.key === " ")) {
        e.preventDefault();
        setModalOpen(true);
      }
    },
    [interactive, hasImages, touch, show, index],
  );

  return (
    <div
      className={cn("relative shrink-0 outline-none", className)}
      aria-label="Product images"
      data-testid={testId}
      tabIndex={interactive && hasImages ? 0 : undefined}
      onKeyDown={onGalleryKeyDown}
    >
      <div
        ref={mainRef}
        className={cn(
          "rounded-[10px] bg-white grid place-items-center overflow-hidden relative border",
          !interactive
            ? "cursor-inherit pointer-events-none"
            : hasImages && !touch
              ? "cursor-crosshair"
              : hasImages
                ? "cursor-pointer"
                : "cursor-default",
          compact && "rounded-[8px]",
        )}
        style={{ borderColor: LINE, width: size, height: size }}
        onMouseEnter={interactive ? onEnter : undefined}
        onMouseMove={interactive ? onMove : undefined}
        onMouseLeave={interactive ? onLeave : undefined}
        onClick={interactive ? onMainClick : undefined}
        role={interactive && hasImages && touch ? "button" : undefined}
        aria-hidden={!interactive ? true : undefined}
        aria-label={
          interactive
            ? hasImages
              ? `${title || "Product"} image`
              : "No product image"
            : undefined
        }
      >
        {hasImages ? (
          <img
            src={current}
            alt={title || "Product image"}
            className="w-full h-full object-contain pointer-events-none rounded-[inherit]"
            draggable={false}
            loading="lazy"
            decoding="async"
          />
        ) : (
          placeholder || <Package className="h-7 w-7 text-ink-muted" aria-hidden />
        )}
      </div>

      <ProductImageMagnifierPreview
        anchorRef={mainRef}
        src={current}
        open={Boolean(interactive && hasImages && !touch && previewOpen)}
        title={title}
        focal={focal}
        compact={compact}
      />

      {multi && showNav && (
        <div className="flex items-center justify-center gap-1.5 sm:gap-2 my-1.5 max-[560px]:my-1">
          <button
            type="button"
            className="w-7 h-7 max-[560px]:w-6 max-[560px]:h-6 rounded-lg border bg-white text-[17px] font-extrabold grid place-items-center hover:border-[#1266dc] hover:text-[#1266dc]"
            style={{ borderColor: LINE, color: NAVY }}
            aria-label="Previous image"
            onClick={() => show(index - 1)}
          >
            ‹
          </button>
          <span className="text-[11px] sm:text-xs min-w-[32px] text-center tabular-nums" style={{ color: MUTED }}>
            {index + 1} / {list.length}
          </span>
          <button
            type="button"
            className="w-7 h-7 max-[560px]:w-6 max-[560px]:h-6 rounded-lg border bg-white text-[17px] font-extrabold grid place-items-center hover:border-[#1266dc] hover:text-[#1266dc]"
            style={{ borderColor: LINE, color: NAVY }}
            aria-label="Next image"
            onClick={() => show(index + 1)}
          >
            ›
          </button>
        </div>
      )}

      {multi && showThumbnails && (
        <div className="flex gap-1 justify-center flex-wrap max-[560px]:gap-0.5">
          {list.map((src, i) => (
            <button
              key={src}
              type="button"
              className="w-[29px] h-[29px] max-[560px]:w-6 max-[560px]:h-6 rounded-[7px] bg-white overflow-hidden p-0.5 border-2"
              style={{ borderColor: i === index ? BLUE : "transparent" }}
              aria-label={`View image ${i + 1}`}
              aria-current={i === index ? "true" : undefined}
              onClick={() => show(i)}
            >
              <img src={src} alt="" className="w-full h-full object-contain" />
            </button>
          ))}
        </div>
      )}

      {modalOpen && hasImages && createPortal(
        <div
          className="fixed inset-0 z-[100] bg-black/50 grid place-items-center p-4"
          onClick={() => setModalOpen(false)}
          role="dialog"
          aria-modal="true"
          aria-label="Product image"
        >
          <div
            className="relative max-w-[min(92vw,520px)] max-h-[min(88vh,520px)] bg-white rounded-xl p-3 shadow-lg"
            style={{ border: `1px solid ${LINE}` }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="absolute -top-3 -right-3 h-8 w-8 rounded-full bg-white border grid place-items-center shadow"
              style={{ borderColor: LINE, color: NAVY }}
              aria-label="Close image"
              onClick={() => setModalOpen(false)}
            >
              <X className="h-4 w-4" />
            </button>
            <img src={current} alt={title || "Product image"} className="max-w-full max-h-[min(80vh,480px)] object-contain mx-auto" />
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}

export default ProductImageLoupe;
